// Side-effect free on purpose: the language pickers import it without initialising i18next.
export const LANGUAGE_OPTIONS = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
] as const;

export type SupportedLanguage = (typeof LANGUAGE_OPTIONS)[number]['code'];

export const SUPPORTED_LANGUAGES: readonly SupportedLanguage[] = LANGUAGE_OPTIONS.map((l) => l.code);

/**
 * Plural forms each language needs beyond French's `_one` / `_other`. i18next picks the suffix from
 * `Intl.PluralRules`: Polish counts use `few` (2-4, 22-24…) and `many` (0, 5-21…), so a bundle
 * holding only `_one` / `_other` would show the wrong form for most numbers.
 */
export const EXTRA_PLURAL_FORMS: Partial<Record<SupportedLanguage, readonly string[]>> = {
  pl: ['few', 'many'],
};

/** Keys a language's bundle must hold, given the flattened French keys: plurals gain the extra forms. */
export function expectedKeysFor(frKeys: Iterable<string>, language: SupportedLanguage): Set<string> {
  const reference = new Set(frKeys);
  const expected = new Set(reference);
  const extraForms = EXTRA_PLURAL_FORMS[language] ?? [];
  for (const key of reference) {
    if (!key.endsWith('_other')) continue;
    const base = key.slice(0, -'_other'.length);
    // `conflict_choose_other` is a plain key: only a `_one` sibling marks a plural.
    if (!reference.has(`${base}_one`)) continue;
    for (const form of extraForms) expected.add(`${base}_${form}`);
  }
  return expected;
}
